// Pamięć profili w tej przeglądarce: który profil jest wybrany, ostatnia znana lista profili, kopia postępu każdego profilu i stan synchronizacji.
// Prawdziwym źródłem postępu jest serwer (wyczyszczenie przeglądarki niczego nie kasuje) — to jest tylko kopia robocza, dzięki której aplikacja
// działa bez internetu i startuje od razu.
//
// Klucze trwałe (localStorage): naative.profiles.v1 (lista), naative.progress.v1.<id>, naative.sync.v1.<id>, naative.pin.v1.<id> (weryfikator PIN-u do pracy bez internetu),
// naative.look.v1 (wygląd w trybie lokalnym, bez serwera), naative.mode.v1.
// Klucz sesji (sessionStorage, patch-34): naative.session.v1 — kto jest zalogowany w tej karcie, czy widział powitanie i na jakim jest ekranie.
// Sesja przeżywa odświeżenie, ale nie zamknięcie aplikacji: świeży start zawsze pokazuje wybór profilu (z PIN-em).
// Gdy przeglądarka nie pozwala zapisywać (tryb prywatny), wszystko żyje w pamięci karty.
import { cleanAvatarId, lookOf } from './look.ts';
import type { Look } from './look.ts';
import { emptyProgress } from './progress.ts';
import type { KeyValueStore } from './progress.ts';
import { validateProgress } from './progressMerge.ts';
import type { ProfileInfo } from './profilesApi.ts';
import { parseProfile } from './profilesApi.ts';
import { HOME_ROUTE, LOCAL_PROFILE, parseSession } from './route.ts';
import type { Route, SessionState } from './route.ts';
import type { Progress } from '../types.ts';

export const SESSION_KEY = 'naative.session.v1';
export const PROFILES_KEY = 'naative.profiles.v1';
export const LOOK_KEY = 'naative.look.v1';
export const MODE_KEY = 'naative.mode.v1';
export const progressKey = (id: string): string => `naative.progress.v1.${id}`;
export const syncKey = (id: string): string => `naative.sync.v1.${id}`;
export const pinKey = (id: string): string => `naative.pin.v1.${id}`;

/** Stan synchronizacji jednego profilu na tym urządzeniu. */
export interface SyncMeta {
  /** „Epoka” postępu na serwerze, którą to urządzenie ostatnio widziało; null = jeszcze nie synchronizowało tego profilu. */
  epoch: number | null;
  /** Są zmiany, których serwer jeszcze nie ma. */
  dirty: boolean;
  /** Kiedy ostatnio udało się zsynchronizować (ISO). */
  syncedAt: string | null;
}

export const NEVER_SYNCED: SyncMeta = { epoch: null, dirty: false, syncedAt: null };

/** `local` = ten adres nie ma serwera (aplikacja działa na jednej przeglądarce); `family` = są konta rodziny i profile. */
export type AppMode = 'local' | 'family';

export interface ProfileStore {
  /** Ostatnio ustalony tryb — pozwala wystartować bez internetu we właściwym trybie. */
  mode(): AppMode | null;
  setMode(m: AppMode): void;
  /** Id profilu wybranego w tej sesji (karcie) albo null (świeży start, „Zmień profil”, „Wyloguj”). W trybie lokalnym też null. */
  selected(): string | null;
  /** Zaczyna sesję profilu (powitanie jeszcze niewidziane, ekran główny); ten sam profil nie zeruje trwającej sesji. `null` kończy sesję. */
  select(id: string | null): void;
  /** Cała sesja: profil, powitanie i ekran (null = nie ma sesji). */
  session(): SessionState | null;
  /** Zapamiętuje ekran, na którym jest dziecko (ignorowane bez sesji). */
  setRoute(route: Route): void;
  /** Zapamiętuje, że dziecko widziało już powitanie „Zaczynamy” w tej sesji. */
  markWelcomed(): void;
  /** Skrót PIN-u profilu zapamiętany po udanym sprawdzeniu — pozwala wejść do profilu bez internetu. */
  pinVerifier(id: string): string | null;
  setPinVerifier(id: string, digest: string | null): void;
  /** Ostatnia lista profili widziana na serwerze (do startu bez internetu). */
  cachedProfiles(): ProfileInfo[];
  cacheProfiles(list: readonly ProfileInfo[]): void;
  /** Czy to urządzenie ma już jakąkolwiek kopię postępu tego profilu. */
  hasProgress(id: string): boolean;
  loadProgress(id: string): Progress;
  /** false = nie udało się zapisać na stałe (kopia żyje do zamknięcia karty). */
  saveProgress(id: string, p: Progress): boolean;
  syncMeta(id: string): SyncMeta;
  setSyncMeta(id: string, meta: SyncMeta): void;
  /** Wygląd w trybie lokalnym (bez serwera i profili). */
  localLook(): Look;
  setLocalLook(look: Look): void;
  /** Usuwa kopię postępu i stan synchronizacji profilu (np. po zarchiwizowaniu go przez rodzica). */
  forget(id: string): void;
}

function browserStorage(): KeyValueStore | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}

function browserSession(): KeyValueStore | null {
  try {
    return typeof sessionStorage === 'undefined' ? null : sessionStorage;
  } catch {
    return null;
  }
}

const safeId = (id: string): boolean => /^[A-Za-z0-9_-]{1,40}$/.test(id);

/** Pamięć z zapasem w pamięci karty: gdy zapis się nie uda (tryb prywatny, pełna pamięć), wartość zostaje w pamięci i odczyt ją widzi. */
function keyValue(store: KeyValueStore | null) {
  const memory = new Map<string, string>();
  const read = (key: string): string | null => {
    if (memory.has(key)) return memory.get(key) ?? null;
    try {
      return store?.getItem(key) ?? null;
    } catch {
      return null;
    }
  };
  const write = (key: string, value: string): boolean => {
    try {
      if (!store) throw new Error('brak pamięci');
      store.setItem(key, value);
      memory.delete(key);
      return true;
    } catch {
      memory.set(key, value);
      return false;
    }
  };
  const remove = (key: string): void => {
    memory.delete(key);
    try {
      store?.removeItem?.(key);
    } catch {
      /* ignore */
    }
  };
  const json = (key: string): unknown => {
    const raw = read(key);
    if (!raw) return null;
    try {
      return JSON.parse(raw) as unknown;
    } catch {
      return null;
    }
  };
  return { read, write, remove, json };
}

export function createProfileStore(store: KeyValueStore | null = browserStorage(), sessionStore: KeyValueStore | null = browserSession()): ProfileStore {
  const { read, write, remove, json } = keyValue(store);
  const ses = keyValue(sessionStore);
  const readSession = (): SessionState | null => parseSession(ses.json(SESSION_KEY));
  const writeSession = (s: SessionState): void => void ses.write(SESSION_KEY, JSON.stringify(s));

  return {
    mode() {
      const v = read(MODE_KEY);
      return v === 'local' || v === 'family' ? v : null;
    },
    setMode(m) {
      write(MODE_KEY, m);
    },
    selected() {
      const v = readSession()?.profile ?? null;
      return v !== null && v !== LOCAL_PROFILE ? v : null;
    },
    select(id) {
      if (id === null) {
        ses.remove(SESSION_KEY);
        return;
      }
      if (!safeId(id)) return;
      if (readSession()?.profile === id) return; // odświeżenie albo ponowne otwarcie tego samego profilu nie zeruje sesji
      writeSession({ profile: id, welcomed: false, route: HOME_ROUTE });
    },
    session: readSession,
    setRoute(route) {
      const cur = readSession();
      if (cur) writeSession({ ...cur, route });
    },
    markWelcomed() {
      const cur = readSession();
      if (cur) writeSession({ ...cur, welcomed: true });
    },
    pinVerifier(id) {
      const v = safeId(id) ? read(pinKey(id)) : null;
      return v && /^[0-9a-f]{64}$/.test(v) ? v : null;
    },
    setPinVerifier(id, digest) {
      if (!safeId(id)) return;
      if (digest === null) remove(pinKey(id));
      else write(pinKey(id), digest);
    },
    cachedProfiles() {
      const raw = json(PROFILES_KEY);
      return Array.isArray(raw) ? raw.map(parseProfile).filter((p): p is ProfileInfo => p !== null) : [];
    },
    cacheProfiles(list) {
      write(PROFILES_KEY, JSON.stringify(list));
    },
    hasProgress(id) {
      return safeId(id) && read(progressKey(id)) !== null;
    },
    loadProgress(id) {
      if (!safeId(id)) return emptyProgress();
      return validateProgress(json(progressKey(id))) ?? emptyProgress();
    },
    saveProgress(id, p) {
      return safeId(id) && write(progressKey(id), JSON.stringify(p));
    },
    syncMeta(id) {
      const r = json(syncKey(id)) as Partial<SyncMeta> | null;
      if (!r || typeof r !== 'object') return { ...NEVER_SYNCED };
      return {
        epoch: typeof r.epoch === 'number' && Number.isInteger(r.epoch) && r.epoch >= 0 ? r.epoch : null,
        dirty: r.dirty === true,
        syncedAt: typeof r.syncedAt === 'string' ? r.syncedAt : null,
      };
    },
    setSyncMeta(id, meta) {
      if (safeId(id)) write(syncKey(id), JSON.stringify(meta));
    },
    localLook() {
      return lookOf(json(LOOK_KEY));
    },
    setLocalLook(look) {
      write(LOOK_KEY, JSON.stringify({ avatar: cleanAvatarId(look.avatar) ?? '', theme: lookOf(look).theme }));
    },
    forget(id) {
      if (!safeId(id)) return;
      remove(progressKey(id));
      remove(syncKey(id));
      remove(pinKey(id));
    },
  };
}
